import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, message, Card, Space, Tag,
  Popconfirm, ColorPicker
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../utils/axios';

const RatingsSettings = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRating, setEditingRating] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/ratings');
      setRatings(response.data);
    } catch (error) {
      message.error('获取评级列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRating(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRating(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ratings/${id}`);
      message.success('删除成功');
      fetchRatings();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 确保颜色值是有效的十六进制格式
      let colorValue = values.color;
      if (colorValue && typeof colorValue === 'object' && colorValue.toHexString) {
        // 如果是从 ColorPicker 返回的对象，转换为十六进制字符串
        colorValue = colorValue.toHexString();
      } else if (colorValue && typeof colorValue === 'string') {
        // 如果是字符串，确保是有效的十六进制格式
        if (!/^#[0-9A-Fa-f]{6}$/.test(colorValue)) {
          message.error('颜色格式不正确，请选择有效的颜色');
          return;
        }
      }

      const submitData = {
        ...values,
        color: colorValue
      };

      if (editingRating) {
        await api.put(`/ratings/${editingRating.id}`, submitData);
        message.success('更新成功');
      } else {
        await api.post('/ratings', submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchRatings();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '评级名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={record.color}>{text}</Tag>
      )
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      sorter: (a, b) => a.level - b.level
    },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      render: (color) => (
        <div style={{ 
          width: 20, 
          height: 20, 
          backgroundColor: color, 
          borderRadius: 4,
          border: '1px solid #d9d9d9'
        }} />
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个评级吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>评级管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加评级
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={ratings}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
          defaultPageSize: 20
        }}
      />

      <Modal
        title={editingRating ? '编辑评级' : '添加评级'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="评级名称"
            rules={[{ required: true, message: '请输入评级名称' }]}
          >
            <Input placeholder="请输入评级名称" />
          </Form.Item>

          <Form.Item
            name="level"
            label="等级"
            rules={[{ required: true, message: '请输入等级' }]}
          >
            <InputNumber 
              placeholder="请输入等级" 
              min={1} 
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="color"
            label="评级颜色"
            initialValue="#28a745"
          >
            <ColorPicker />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRating ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RatingsSettings; 